import { Box, Stack } from '@mui/material';
import { format } from 'date-fns';
import { Fragment } from 'react/jsx-runtime';

import PencilIcon from '@/assets/svg/ui-elements/pencil.svg';
import { Badge, Button, Separator, theme, Typography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { useFilesById } from '@/shared/hooks/use-files-by-id';
import { useEntitySlice } from '@/stores/selectors';

import {
  confirmationTitles,
  createYourEntityLabels,
  directorStatusBadges,
  legalEntityTypeOptions,
  registeredAddressLabels,
  selectClassOptions,
  typeToTitleMap,
  VerificationType,
} from '../common';
import {
  FieldGrid,
  FormPanel,
  LinksContainer,
  PanelHeader,
  ReviewField,
  ReviewRow,
  ReviewRowLabel,
  SectionHeading,
} from '../styled';
import { useCreateEntity } from '../use-create-entity';
import { useEntityReviewMode } from '../use-entity-review-mode';
import { LongText } from './long-text';
import { useDirectorRows } from './use-director-rows';
import { VerificationFileCard } from './verification-file-card';
import { UpdatedBadge } from './wizard-status-badges';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body3" color={theme.colors.text.secondary}>
    {children}
  </Typography>
);

const FieldValue = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2">{children}</Typography>
);

export const FirstStep = () => {
  const isMobile = useIsMobile();
  const { forms: stepForms } = useEntityReviewMode();
  const { countrySelectOptions, regionSelectOptions } = useCreateEntity();

  const formatFieldValue = (fieldName: string) => {
    const value = stepForms[fieldName as keyof typeof stepForms] as string;

    if (fieldName === 'registeredAt') {
      return value ? format(value, 'dd/MM/yyyy') : '-';
    }

    if (fieldName === 'type') {
      const type = legalEntityTypeOptions.find((el) => el.value === value);
      return type?.label || '-';
    }
    if (fieldName === 'country' && value) {
      const option = countrySelectOptions.find((c) => c.value === value);
      return option ? option.label : '-';
    }

    if (fieldName === 'state' && value) {
      const option = regionSelectOptions.options.find((r) => r.value === value);
      return option ? option.label : '-';
    }

    return value || '-';
  };

  const fields = [...createYourEntityLabels, ...registeredAddressLabels];

  return (
    <FieldGrid $columns={isMobile ? 1 : 2}>
      {fields.map((field) => (
        <ReviewField key={field.name}>
          <FieldLabel>{field.label}</FieldLabel>
          <LongText text={formatFieldValue(field.name)} />
        </ReviewField>
      ))}
    </FieldGrid>
  );
};

export const SecondStep = () => {
  const isMobile = useIsMobile();
  const { stepForms } = useEntitySlice();
  const { rows } = useDirectorRows(stepForms.id);

  return (
    <>
      {rows.map((row, index) => {
        const badge = directorStatusBadges[row.status];

        return (
          <Fragment key={row.key}>
            <Stack flexDirection="column" gap={`${theme.spacing[16]}px`}>
              <Typography variant="body2">{`Director ${index + 1}:`}</Typography>

              <FieldGrid $columns={isMobile ? 1 : 2}>
                <ReviewField>
                  <FieldLabel>Director</FieldLabel>
                  <FieldValue>{row.name || '-'}</FieldValue>
                </ReviewField>

                <ReviewField>
                  <FieldLabel>Email</FieldLabel>
                  <LongText text={row.email || '-'} />
                </ReviewField>

                <ReviewField>
                  <FieldLabel>Invitation</FieldLabel>
                  <Box>
                    <Badge label={badge.label} color={badge.color} variant="subtle" />
                  </Box>
                </ReviewField>
              </FieldGrid>
            </Stack>

            {index < rows.length - 1 && <Separator />}
          </Fragment>
        );
      })}
    </>
  );
};

export const ThirdStep = () => {
  const isMobile = useIsMobile();
  const { forms } = useEntityReviewMode();
  const { shareClasses } = forms.shareStructure;

  return (
    <>
      {shareClasses.map((el, index) => {
        const className = selectClassOptions.find((cl) => cl.value === el.class)?.label;
        const totalAllocated =
          el.shareholders?.reduce((sum, cls) => sum + (cls.numberOfShares ?? 0), 0) ?? 0;
        const allocatedPercent =
          el?.numberOfShares && totalAllocated > 0
            ? `${((totalAllocated / el.numberOfShares) * 100).toFixed(2)}%`
            : '-';

        return (
          <Fragment key={index}>
            <Stack flexDirection="column" gap={`${theme.spacing[16]}px`}>
              <Typography variant="body2">{`#${index + 1} Share Class`}</Typography>

              <FieldGrid $columns={isMobile ? 1 : 2}>
                <ReviewField>
                  <FieldLabel>Class</FieldLabel>
                  <FieldValue>{className ?? '-'}</FieldValue>
                </ReviewField>

                <ReviewField>
                  <FieldLabel>Total # of Shares</FieldLabel>
                  <FieldValue>{el.numberOfShares ? `${el.numberOfShares} Shares` : '-'}</FieldValue>
                </ReviewField>
              </FieldGrid>

              {el.shareholders?.map((sh, shareholderIndex) => {
                const percentage =
                  (el?.numberOfShares ?? 0) > 0
                    ? `${(((sh.numberOfShares ?? 0) / (el.numberOfShares ?? 0)) * 100).toFixed(2)}%`
                    : '-';

                return (
                  <ReviewRow key={shareholderIndex}>
                    <ReviewField>
                      <FieldLabel>Type</FieldLabel>
                      <FieldValue>{sh.type ?? '-'}</FieldValue>
                    </ReviewField>

                    <ReviewField>
                      <FieldLabel>Full Name</FieldLabel>
                      <FieldValue>{sh.fullName ?? '-'}</FieldValue>
                    </ReviewField>

                    <ReviewField>
                      <FieldLabel>Email address</FieldLabel>
                      <LongText text={sh.email ?? '-'} />
                    </ReviewField>

                    <ReviewField>
                      <FieldLabel>Number of Shares</FieldLabel>
                      <FieldValue>{sh.numberOfShares ?? '-'}</FieldValue>
                    </ReviewField>

                    <ReviewField>
                      <FieldLabel>%</FieldLabel>
                      <FieldValue>{percentage}</FieldValue>
                    </ReviewField>
                  </ReviewRow>
                );
              })}

              <ReviewRow>
                <ReviewRowLabel>
                  <Typography variant="body2">
                    {`Aggregated figures for ${className ?? 'this class'}:`}
                  </Typography>
                </ReviewRowLabel>

                <ReviewField>
                  <FieldLabel>Number of Shares</FieldLabel>
                  <FieldValue>{totalAllocated}</FieldValue>
                </ReviewField>

                <ReviewField>
                  <FieldLabel>%</FieldLabel>
                  <FieldValue>{allocatedPercent}</FieldValue>
                </ReviewField>
              </ReviewRow>
            </Stack>

            {index < shareClasses.length - 1 && <Separator />}
          </Fragment>
        );
      })}
    </>
  );
};

export const FourthStep = () => {
  const isMobile = useIsMobile();
  const { forms } = useEntityReviewMode();
  const verificationsDocuments = forms?.verifications.filter((el) => el.format === 'file') ?? [];
  const verificationsLinks = forms?.verifications.filter((el) => el.format === 'url') ?? [];

  const filesById = useFilesById(verificationsDocuments.map((doc) => doc.fileId));

  const groupedDocs = verificationsDocuments.reduce(
    (acc, doc) => {
      if (!acc[doc.type]) acc[doc.type] = { files: [], links: [] };
      acc[doc.type].files.push(doc);
      return acc;
    },
    {} as Record<
      string,
      { files: typeof verificationsDocuments; links: typeof verificationsLinks }
    >,
  );

  verificationsLinks.forEach((link) => {
    if (!groupedDocs[link.type]) groupedDocs[link.type] = { files: [], links: [] };
    groupedDocs[link.type].links.push(link);
  });

  return (
    <FieldGrid $columns={isMobile ? 1 : 2}>
      {Object.entries(groupedDocs).map(([type, data]) => (
        <Stack
          key={type}
          flexDirection="column"
          gap={`${theme.spacing[16]}px`}
          width="100%"
          overflow="hidden"
        >
          <Typography variant="body2">{typeToTitleMap[type as VerificationType]}</Typography>

          {data.files.length > 0 && (
            <Stack flexDirection="column" gap={`${theme.spacing[8]}px`}>
              {data.files.map((file) => {
                const meta = file.fileId ? filesById[file.fileId] : undefined;
                return <VerificationFileCard key={file.id} file={file} url={meta?.url} />;
              })}
            </Stack>
          )}

          {data.links.length > 0 && (
            <Stack flexDirection="column" gap={`${theme.spacing[8]}px`}>
              <FieldLabel>Link</FieldLabel>

              {data.links.map((link) => (
                <LinksContainer key={link.id}>
                  <Typography variant="body2" noWrap>
                    {link.url}
                  </Typography>
                </LinksContainer>
              ))}
            </Stack>
          )}
        </Stack>
      ))}
    </FieldGrid>
  );
};

const stepToComponent: Record<number, React.ElementType> = {
  1: FirstStep,
  2: SecondStep,
  3: ThirdStep,
  4: FourthStep,
};

export const Confirmation = () => {
  const { setStep } = useEntitySlice();
  const { isReviewMode, isStepChanged, highlightIf } = useEntityReviewMode();

  return (
    <>
      {confirmationTitles.map((section) => {
        const StepComponent = stepToComponent[section.step];

        return (
          <FormPanel key={section.step} $changed={highlightIf(isStepChanged(section.step))}>
            <PanelHeader>
              <SectionHeading>
                <Typography variant="h6">{section.title}</Typography>
                {isStepChanged(section.step) && <UpdatedBadge />}
              </SectionHeading>

              {!isReviewMode && (
                <Button
                  variant="subtle"
                  color="primary"
                  size="sm"
                  onClick={() => setStep(section.step)}
                >
                  <PencilIcon />
                  Edit
                </Button>
              )}
            </PanelHeader>

            <Separator />

            <StepComponent />
          </FormPanel>
        );
      })}
    </>
  );
};
